import { ReportDataSource } from 'common/types/reportFilters';

import { Story, BlockDict, StorySerialized, Block, StoryData } from 'types';
import { getBlock } from './Blocks';
import { StorytellerState } from 'store/StorytellerReduxState';
import { storyReducerBlocksSelector, storyReducerStoriesSelector } from 'store/TopLevelSelector';
import {
  getDataSourceForSerialize,
  getDraftFilterParameterConfigurations,
  getHasUserMadeFilterChanges,
} from 'store/selectors/DataSourceSelectors';
import { createSelector } from 'reselect';
import { set } from 'lodash/fp';
import { FeatureFlags } from '@socrata/platform/feature-flags';

export const serializeStoryFromEnvironment = (storyData: StoryData): StorySerialized => {
  return {
    ...serializeStory(storyData),
    blocks: storyData.blocks.map(serializeBlock),
  };
};

export const serializeStoryFromState = (
  story: Story,
  blocks: BlockDict,
  dataSource?: ReportDataSource,
): StorySerialized => {
  return {
    ...serializeStory(story),
    dataSource: dataSource ?? story.dataSource,
    blocks: story.blockIds.map((blockId) => serializeBlockFromState(blockId, blocks)),
  };
};

const serializeStory = (story: StoryData | Story): Omit<StorySerialized, 'blocks'> => {
  return {
    uid: story.uid,
    title: story.title,
    description: story.description,
    dataSource: story.dataSource,
    theme: story.theme,
    layout: story.layout,
  };
};

export const serializeBlockFromState = (blockId: string, blocks: BlockDict) => {
  // NOTE! This _must not_ return any reference
  // to internal data structures! Everything
  // must be a fresh instance. Otherwise, the
  // returned object is a backdoor allowing
  // untracked state changes to this store's state.

  const block = getBlock(blockId, blocks);
  return serializeBlock(block);
};

const serializeBlock = (block: Block) => ({
  layout: block.layout,
  components: block.components,
  presentable: block.presentable,
  background_color: block.background_color,
});

export const selectSerializedStory =
  (storyUid: string) =>
  (state: StorytellerState): StorySerialized => {
    const story = storyReducerStoriesSelector(state)[storyUid];
    const dataSources = getDataSourceForSerialize(state);
    return serializeStoryFromState(story, storyReducerBlocksSelector(state), dataSources);
  };

/**
 * Gets the serialized story for the given UID. If the user has made any changes to the
 * GFB and the user hasn't clicked run report yet, it will return those "draft" filter
 * values as `dataSource.filterParameterConfigurations` instead of what's _currently_ there.
 */
export const selectSerializedDerivedReport = (storyUid: string) =>
  createSelector(
    selectSerializedStory(storyUid),
    getHasUserMadeFilterChanges,
    getDraftFilterParameterConfigurations,
    (serializedStory, filterChanged, draftFilters) => {
      if (!filterChanged || !FeatureFlags.valueOrDefault('enable_batch_running_filters')) {
        return serializedStory;
      }

      return set('dataSource.filterParameterConfigurations', draftFilters, serializedStory);
    },
  );
